import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import classNames from 'classnames';
import { z } from 'zod';

import type { UserBalances } from '@api';

import type {
  RewardPool,
  RewardPoolTicketCurrency,
  RewardPoolTicketCurrencyType} from '@shared/api/types/rewardPools';
import {
  REWARD_POOL_TICKET_CURRENCY
} from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { useRewardPoolSelfEntries } from '@shared/hooks/query/rewardPools';

import { useUserBalancesQuery } from '@query/karma';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  calculateMaxAvailableTickets,
  checkIsInsufficientBalance,
} from '@store:web/components/entities/rewardPool/model/ticketLimitCalculation';
import { useBuyEntry } from '@store:web/components/entities/rewardPool/model/useBuyEntry/useBuyEntry';
import {
  DialogButton,
  DialogButtonsWrapper,
  DialogCancelButton,
} from '@ui/uikit/components/common/Dialog';
import { FormAmountInputWithPresets } from '@ui/uikit/components/common/FormAmountInputWithPresets';
import { formatReward } from '@ui/uikit/utils';

import { DeductionHint } from './DeductionHint';
import { TicketPriceDetails } from './TicketPriceDetails';

const renderHintMessage = (
  currency: RewardPoolTicketCurrency,
  balance: number,
  errorMessage?: string,
) => {
  const message =
    currency === REWARD_POOL_TICKET_CURRENCY.FREE
      ? `Raffle Credits Balance: ${balance}`
      : `Magic ID ${currency} Balance: ${formatReward(balance)}`;

  return (
    <div>
      <div>{message}</div>
      {Boolean(errorMessage) && <div>{errorMessage}</div>}
    </div>
  );
};

const BalanceToTicketCurrencyMap: Partial<Record<RewardPoolTicketCurrency, keyof UserBalances>> = {
  [REWARD_POOL_TICKET_CURRENCY.SQR]: 'sqr',
  [REWARD_POOL_TICKET_CURRENCY.SQRp]: 'sqrp',
  [REWARD_POOL_TICKET_CURRENCY.FREE]: 'raffleTickets',
  [REWARD_POOL_TICKET_CURRENCY.USDT]: 'usdt',
};

type Props = {
  className?: string;
  currencyType: RewardPoolTicketCurrencyType;
  onSubmit: () => void;
  rewardPool: RewardPool;
};

const SHOW_DEPOSIT_FOR_CURRENCIES = [
  REWARD_POOL_TICKET_CURRENCY.SQR,
  REWARD_POOL_TICKET_CURRENCY.USDT,
];

type CheckIsShowDepositButtonProps = {
  currencyType: RewardPoolTicketCurrencyType;
  value: string;
  balance: number;
};

const checkIsShowDepositButton = ({
  currencyType,
  value,
  balance,
}: CheckIsShowDepositButtonProps) => {
  const isInsufficientBalance = checkIsInsufficientBalance({
    price: currencyType.price,
    balance,
    entryAmount: value,
  });

  return (
    isInsufficientBalance &&
    SHOW_DEPOSIT_FOR_CURRENCIES.some((currency) => currencyType.currency === currency)
  );
};

export const GetTicketsForm = ({ className, currencyType, onSubmit, rewardPool }: Props) => {
  const { user } = useAuth();

  const { data: balances } = useUserBalancesQuery(user?.id, { enabled: Boolean(user?.id) });
  const { data: entries = [] } = useRewardPoolSelfEntries(rewardPool.id);

  const { mutateAsync: buyEntry } = useBuyEntry({ rewardPool });
  const { currency, price } = currencyType;

  const key = BalanceToTicketCurrencyMap[currency];
  const balance = key && balances && balances[key] ? balances[key] : 0;

  const maxAvailableTickets = calculateMaxAvailableTickets({
    balance,
    price,
    entryAmount: entries.length,
    rewardPool,
  });

  const FormSchema = useMemo(() => {
    return z.object({
      amount: z
        .string()
        .min(1, { message: 'Field is required.' })
        .transform((value) => parseInt(value, 10))
        .refine((value) => value * price <= balance, { message: 'Insufficient balance.' })
        .transform((value) => value.toString()),
    });
  }, [price, balance]);

  const [defaultValues] = useState(() => {
    return { amount: '' };
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });
  const { handleSubmit, control, formState, reset, watch, trigger } = form;
  const watchedAmount = watch('amount');
  const [showDepositButton, setShowDeposit] = useState(false);

  useEffect(() => {
    const show = checkIsShowDepositButton({
      currencyType,
      balance,
      value: watchedAmount,
    });

    if (watchedAmount) {
      void trigger('amount');
    }

    setShowDeposit(show);
  }, [balance, currencyType, trigger, watchedAmount]);

  const submit = handleSubmit(async (data) => {
    try {
      await buyEntry({ currencyType, entryAmount: Number(data.amount) });
      reset(defaultValues);
      onSubmit();
    } catch (error) {
      displayErrorToast({ error });
    }
  });

  const { isDirty, errors, isSubmitting } = formState;
  const hasErrors = Object.keys(errors).length > 0;
  const isSubmitButtonDisabled = !isDirty || hasErrors || isSubmitting;
  const minAvailableTickets = 1;
  const [amountValue, setAmountValue] = useState('');

  return (
    <form onSubmit={submit} className={classNames('flex flex-col', className)}>
      <FormAmountInputWithPresets
        name="amount"
        label="Select Number of Tickets"
        control={control}
        min={minAvailableTickets}
        max={maxAvailableTickets}
        onChangeValue={setAmountValue}
        renderHintMessage={(errorMessage) => renderHintMessage(currency, balance, errorMessage)}
        presets={{ show: currency !== REWARD_POOL_TICKET_CURRENCY.FREE }}
      >
        <>
          <TicketPriceDetails currencyType={currencyType} ticketsAmount={Number(amountValue)} />

          <DeductionHint currency={currency} />
        </>
      </FormAmountInputWithPresets>
      <DialogButtonsWrapper>
        <DialogCancelButton />
        {showDepositButton ? (
          <DialogButton href="/profile/membership/dashboard" target="_blank">
            Deposit {currencyType.currency}
          </DialogButton>
        ) : (
          <DialogButton type="submit" isDisabled={isSubmitButtonDisabled}>
            Confirm
          </DialogButton>
        )}
      </DialogButtonsWrapper>
    </form>
  );
};
